// ============================================================================
// BarSpiritList — the brand lists a restaurant's bar rings from (design §6)
// ----------------------------------------------------------------------------
// A restaurant turns on the lists it wants (Vodka, Rum, … Mixer, Style) and
// OrderUp generates a REAL modifier group for each in the restaurant's own
// `modifier_groups`, one option per bottle it carries. The POS renders them
// like any other group — MIXER → VODKA → VODKA SUB with no POS change — and the
// order-close resolver substitutes and doubles pours from the
// `modifier_ingredients` docs the generator writes beside each option.
//
// This file is the PURE half: what a list contains, in what order, at what
// price and with which draw. `barSpiritListService` (packages/core) applies it.
//
// ── OrderUp never sets a price ──────────────────────────────────────────────
// Every spirit, liqueur and mixer option is priced ONE AT A TIME by the
// restaurant (owner decision 2026-09-29: "not every spirit is a call … the
// restaurant should configure each spirit by price as they see fit"). That
// price lives on the option's own modifier `price`, written by the server
// when the Spirit lists tab saves it — so the planner never prices a spirit
// or mixer option at all (`price: null`), and a new option is always held
// OFF until the restaurant prices it. Only the five Style options and the
// Double option take a price from the settings (`stylePrices`, `doublePrice`
// — the Double price is an UPCHARGE on the style picked). There are no tiers:
// a catalog bottle's tier neither prices nor orders anything here.
//
// ── House by default (bar v2, design A1) ────────────────────────────────────
// A spirit list is the CALL list: every carried bottle of that type EXCEPT the
// house bottle (`houseInventoryGuid`). No pick IS the house pour — nothing to
// tap and nothing to print.
//
// ── Quantities are not prices ───────────────────────────────────────────────
// `basePourOz` (default 1.5), `stylePours`, `doubleMultiplier` and
// `BAR_MIXER_TOP_OZ` are how much a pick POURS. A style whose pour differs
// from the base multiplies every spirit draw of the line by
// `stylePour / basePour`; Double multiplies by `doubleMultiplier` on top.
// ============================================================================

import {
  BAR_SPIRIT_TYPES,
  BAR_SPIRIT_TYPE_LABELS,
  barBottleDisplayName,
  formatBarBottleSize,
  type BarMixerDispense,
  type BarSpiritType,
  type IBarBottle,
} from "./BarBottle";
import { resolveBarBottleImage } from "./barBottleImage";
import { normalizeBarRowName } from "./barRowNames";

/**
 * One generated list: a spirit family (its call brands), the mixers, the
 * serving styles, or Double — an upcharge on the style, never a style itself.
 */
export type BarListKey = BarSpiritType | "mixer" | "style" | "double";

export const BAR_LIST_KEYS: readonly BarListKey[] = [
  ...BAR_SPIRIT_TYPES,
  "mixer",
  "style",
  "double",
] as const;

export const BAR_LIST_LABELS: Readonly<Record<BarListKey, string>> = {
  ...BAR_SPIRIT_TYPE_LABELS,
  mixer: "Mixer",
  style: "Style",
  double: "Double",
};

/**
 * Lists whose options are priced from the SETTINGS (`stylePrices`,
 * `doublePrice`), never one at a time on the Spirit lists tab.
 */
export function isBarSettingsPricedListKey(key: BarListKey): key is "style" | "double" {
  return key === "style" || key === "double";
}

export function isBarSpiritListKey(key: BarListKey): key is BarSpiritType {
  return (BAR_SPIRIT_TYPES as readonly string[]).includes(key);
}

export type BarStyleKey = "rocks" | "neat" | "up" | "tall" | "shot";

export const BAR_STYLE_KEYS: readonly BarStyleKey[] = [
  "rocks",
  "neat",
  "up",
  "tall",
  "shot",
] as const;

/**
 * Style keys a generated option may still carry from before bar v2
 * (2026-09-30): Double was a style. Such an option is archived by the
 * generator and never planned again; the key stays readable so a stored
 * option (and the modifier editor that saves it) still validates.
 */
export const BAR_RETIRED_STYLE_KEYS = ["double"] as const;
export type BarRetiredStyleKey = (typeof BAR_RETIRED_STYLE_KEYS)[number];

/**
 * The fixed Style list: an instruction to the bartender that also sets the
 * pour (`stylePours`). Double is NOT here — it is its own list, an upcharge
 * on the style picked.
 */
export const BAR_STYLE_OPTIONS: ReadonlyArray<{
  readonly key: BarStyleKey;
  readonly label: string;
}> = [
  { key: "rocks", label: "Rocks" },
  { key: "neat", label: "Neat" },
  { key: "up", label: "Up" },
  { key: "tall", label: "Mixed (with a mixer)" },
  { key: "shot", label: "Shot" },
] as const;

/**
 * The style key that is no longer a STYLE (owner, 2026-10-03): "the style and
 * mixer having 2 different groups on the POS is redundant, it should be in
 * one". A mixer choice IS the tall drink, so Tall is not rung as a pick any
 * more. Its settings stay — `stylePrices.tall` is the location's ONE standard
 * "Mixed (with a mixer)" price, `stylePours.tall` what a mixed drink pours,
 * `styleInstructions.tall` how it is built — and each mixer option on the
 * required Style group rings that price (plus its own upcharge) and pours that
 * pour. The `bar_style_tall` option document is kept ARCHIVED (never on a
 * group, never deleted: past orders name it) as the carrier of the per-menu
 * Mixed price (`modifierPriceOverrides` keyed by its modifierId).
 */
export const BAR_MIXED_STYLE_KEY = "tall" as const satisfies BarStyleKey;

/** The styles a guest still picks by name on the Style group: Rocks, Neat, Up, Shot. */
export const BAR_RINGING_STYLE_KEYS: readonly BarStyleKey[] = BAR_STYLE_KEYS.filter((k) => k !== BAR_MIXED_STYLE_KEY);

/** The one option of the Double list. */
export const BAR_DOUBLE_OPTION_LABEL = "Double";

/** What a style instruction is written for: each style, and Double. */
export type BarStyleInstructionKey = BarStyleKey | "double";
export const BAR_STYLE_INSTRUCTION_KEYS: readonly BarStyleInstructionKey[] = [...BAR_STYLE_KEYS, "double"];

/** Longest style instruction the settings keep — a few lines a bartender reads at a glance. */
export const BAR_STYLE_INSTRUCTIONS_MAX = 500;

/**
 * OrderUp's standard HOW-TO for each style and for Double (owner,
 * 2026-10-03) — glass, ice, pour or build. Shown at the TILL only, never
 * printed on a chit, a KDS or a receipt. Every location starts from this text
 * and may rewrite it (a bar that only stocks rocks glasses rewrites Tall),
 * and its own text is then the standard for that whole location
 * (`IBarSpiritSettings.styleInstructions`). No prices: OrderUp never sets one.
 */
export const BAR_STYLE_STANDARD_INSTRUCTIONS: Readonly<Record<BarStyleInstructionKey, string>> = {
  rocks: "Rocks glass. Fill with ice, pour the spirit over the ice.",
  neat: "Rocks glass or snifter. No ice — pour at room temperature.",
  up: "Shake or stir with ice, strain into a chilled coupe or martini glass. No ice in the glass.",
  tall: "Highball glass. Fill with ice. Pour the spirit, top with the mixer.",
  shot: "Shot glass. No ice — pour straight.",
  double: "Double the pour of the style chosen — same glass, same build.",
};

/** A style instruction as stored: trimmed, not empty, at most `BAR_STYLE_INSTRUCTIONS_MAX`. */
export function isValidBarStyleInstruction(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0 && v.trim().length <= BAR_STYLE_INSTRUCTIONS_MAX;
}

/** The base pour of one spirit on a mixed drink, in ounces. A quantity, not a price. */
export const BAR_BASE_POUR_OZ_DEFAULT = 1.5;
/** Largest pour (base or per style) the settings accept — past this it is a typo, not a pour. */
export const BAR_POUR_OZ_MAX = 6;
/**
 * The ORDER-CLOSE cap on one option's pour multiplier — the ONE constant the
 * modifier-ingredient writer (`RECIPEOPS_INVALID_POUR_MULTIPLIER`), the stored
 * `ModifierIngredient` schema and the order-close resolver all enforce. A
 * setting that would give an option a larger multiplier is refused at save,
 * never accepted and then left failing regeneration.
 */
export const BAR_POUR_MULTIPLIER_MAX = 4;
/** What Double pours relative to the style picked, by default (today's Double: × 2). */
export const BAR_DOUBLE_MULTIPLIER_DEFAULT = 2;
/** Largest Double multiplier the settings accept — the order-close cap. The smallest is anything above 1. */
export const BAR_DOUBLE_MULTIPLIER_MAX = BAR_POUR_MULTIPLIER_MAX;

/** A pour in ounces: finite, more than 0, at most `BAR_POUR_OZ_MAX`, two decimals. */
export function isValidBarPourOz(v: unknown): v is number {
  return (
    typeof v === "number" &&
    Number.isFinite(v) &&
    v > 0 &&
    v <= BAR_POUR_OZ_MAX &&
    Math.abs(v * 100 - Math.round(v * 100)) < 1e-6
  );
}

/** A Double multiplier: finite, more than 1, at most `BAR_DOUBLE_MULTIPLIER_MAX`. */
export function isValidBarDoubleMultiplier(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 1 && v <= BAR_DOUBLE_MULTIPLIER_MAX;
}

/**
 * One serving of a mixer poured by the ounce (gun, house-made, or a packaged
 * mixer whose container size is unknown), in ounces — a standard highball top.
 * Only drawn while the restaurant COUNTS that mixer; a gun mixer carries
 * uncounted by default and then draws nothing.
 */
export const BAR_MIXER_TOP_OZ = 4;

/**
 * One generated GROUP. Every list has one Optional group — a spirit list's
 * call brands (0–1; nothing picked pours the house bottle), Mixer (0–2),
 * Style (0–1, on catalog cocktails), Double (0–1). Style alone has a second,
 * REQUIRED group over the SAME options, `style_required` (exactly 1), for the
 * generated mixed-drink items: the POS ignores a reference's `isRequired`
 * override, so one group cannot be both.
 */
export type BarListGroupSlot = BarListKey | "style_required";

export const BAR_LIST_GROUP_SLOTS: readonly BarListGroupSlot[] = [...BAR_LIST_KEYS, "style_required"];

/**
 * The deterministic document id of a generated group in the restaurant's
 * `modifier_groups` — `bar_list_vodka`, `bar_list_style_required`,
 * `bar_list_double`, … (Rule 3, docs/FIRESTORE_DOCUMENT_IDS.md). The id IS the
 * group's identity: nothing stores a reference to a generated group, and a
 * reader that needs its numeric `modifierGroupId` reads the document at this id.
 */
export function barListGroupDocId(slot: BarListGroupSlot): string {
  return `bar_list_${slot}`;
}

/**
 * The ids of the RETIRED per-spirit required groups (`bar_list_vodka_required`,
 * …) — before bar v2 a mixed-drink item had to pick a brand. With a house
 * default the call pick is optional, so these groups are removed from every
 * item that references them and archived (never deleted: past orders name
 * them). Listed so the retirement can find them; nothing generates one again.
 */
export function barRetiredRequiredGroupDocIds(): string[] {
  return BAR_SPIRIT_TYPES.map((key) => `bar_list_${key}_required`);
}

/**
 * A restaurant's spirit-list settings, stored on the restaurant document as
 * the sparse `settings.barSpiritLists` block (`RestaurantBarSpiritListsBlock`).
 */
export interface IBarSpiritSettings {
  /** The lists this restaurant turned on. */
  readonly enabled: BarListKey[];
  /** The restaurant's price per style. Absent = UNPRICED (that style stays off); 0 = free. */
  readonly stylePrices: Partial<Record<BarStyleKey, number>>;
  /**
   * The Double UPCHARGE, added on top of the style picked. Absent = unpriced:
   * the Double option stays off (held); 0 = free.
   */
  readonly doublePrice?: number;
  /** Ounces a style pours, where it differs from the base. Absent = `basePourOz`. */
  readonly stylePours: Partial<Record<BarStyleKey, number>>;
  /** Ounces one spirit pours on a mixed drink with no style pour of its own. A quantity. */
  readonly basePourOz: number;
  /** What Double pours relative to the style picked (1 < x ≤ 4, default 2). */
  readonly doubleMultiplier: number;
  /**
   * Each spirit type's HOUSE ROLE (design A1, Bar → House pours): the platform
   * role (`platformIngredientGuid`) whose holder is that type's house bottle.
   * The bottle itself is still decided by the role — held by exactly one
   * carried item (`assertHousePourFree`) — so a carry, an un-carry heir or a
   * mapping moves the house with the role and nothing here goes stale. This
   * only says WHICH role, for a type with several (White / Dark Rum, Vodka /
   * Citrus Vodka). Written ONLY by `BarSpiritListService.setHousePour`, in the
   * transaction that moves the role. Absent for a type = never set in
   * Settings: `barHousePourFor`'s by-name rule applies.
   */
  readonly houseRoles?: Partial<Record<BarSpiritType, string>>;
  /**
   * The location's OWN how-to text for a style or Double, where it rewrote
   * OrderUp's standard (`BAR_STYLE_STANDARD_INSTRUCTIONS`). Absent for a key =
   * the standard ("Reset to OrderUp standard" removes it). Till only — the
   * generator writes the resolved text onto each Style / Double option's
   * `posDescription` (`barStyleInstructionsFor`).
   */
  readonly styleInstructions?: Partial<Record<BarStyleInstructionKey, string>>;
  /**
   * Which KINDS of carried mixer are offered on the mixed drinks' Style group
   * (owner, 2026-10-03: "turn mixer kinds on/off at my discretion") — Fountain
   * (`gun`), House-made (`house`), Packaged (`packaged`), the `dispense`
   * vocabulary. Absent / false = off. It replaces the old Mixer list switch
   * (`enabled: ['mixer']`): a block still carrying that reads as all three
   * on (`barSpiritSettingsFromJSON`), and the next save stores the kinds and
   * drops `'mixer'` from `enabled`.
   */
  readonly mixerKinds?: BarMixerKinds;
}

/**
 * The mixer kinds offered as a style, by dispense. Stored presence-only: a kind
 * that is on is `true`, a kind that is off is ABSENT (never `false`) — the shape
 * the reader normalises to and the settings schema accepts.
 */
export type BarMixerKinds = Partial<Record<BarMixerDispense, true>>;

/** The three mixer kinds, in the order the Bar page shows them. */
export const BAR_MIXER_KINDS: readonly BarMixerDispense[] = ["gun", "house", "packaged"] as const;

/** Each mixer kind as the Bar page names it. */
export const BAR_MIXER_KIND_LABELS: Readonly<Record<BarMixerDispense, string>> = {
  gun: "Fountain",
  house: "House-made",
  packaged: "Packaged",
};

/** Is any mixer kind offered as a style? */
export function isAnyBarMixerKindOn(settings: Partial<Pick<IBarSpiritSettings, "mixerKinds">>): boolean {
  return BAR_MIXER_KINDS.some((k) => settings.mixerKinds?.[k] === true);
}

/**
 * Every key `restaurant.settings.barSpiritLists` may carry — the field
 * contract of the settings block (docs/SEEDING_DEFAULTS.md, "Settings blocks").
 */
export const LOCATION_BAR_SPIRIT_LISTS_KEYS = [
  "enabled",
  "stylePrices",
  "doublePrice",
  "stylePours",
  "basePourOz",
  "doubleMultiplier",
  "houseRoles",
  "styleInstructions",
  "mixerKinds",
] as const satisfies ReadonlyArray<keyof IBarSpiritSettings>;

/**
 * Keys the block no longer carries. Never written again — the next save of the
 * block DELETES them (`BarSpiritListService`), and `barSpiritSettingsFromJSON`
 * drops them on read. Kept as a list only so the delete can name them.
 *
 * - `tierPrices`, `mixerPrice` — TIER pricing (before 2026-09-29).
 * - `required` — per-spirit "a brand must be picked" (before bar v2,
 *   2026-09-30): a mixed drink with no call pick now pours the house bottle.
 * - `standardPourOz` — renamed `basePourOz`. Read as the base pour while
 *   `basePourOz` is absent, so the value survives until the save that
 *   rewrites it under its new name.
 *
 * `stylePrices.double` (Double was a style) is retired too, inside a kept
 * key: dropped on read, gone on the next write of `stylePrices`, and readable
 * meanwhile through `barRetiredDoubleStylePrice` for the notice only.
 */
export const LOCATION_BAR_SPIRIT_LISTS_RETIRED_KEYS = [
  "tierPrices",
  "mixerPrice",
  "required",
  "standardPourOz",
] as const;

export type LocationBarSpiritListsKey = (typeof LOCATION_BAR_SPIRIT_LISTS_KEYS)[number];

/**
 * `restaurant.settings.barSpiritLists` as STORED — SPARSE: an absent key is its
 * `defaultBarSpiritSettings()` value, and a key saved back to its default is
 * deleted. SERVER-WRITTEN only (`BarSpiritListService.saveSettings`, firestore
 * rules `serverOnlySettingsBlocks`); read through `barSpiritSettingsFromJSON`.
 */
export type LocationBarSpiritListsBlock = Partial<
  Pick<IBarSpiritSettings, LocationBarSpiritListsKey>
>;

export function defaultBarSpiritSettings(): IBarSpiritSettings {
  return {
    enabled: [],
    stylePrices: {},
    stylePours: {},
    basePourOz: BAR_BASE_POUR_OZ_DEFAULT,
    doubleMultiplier: BAR_DOUBLE_MULTIPLIER_DEFAULT,
  };
}

const isListKey = (k: unknown): k is BarListKey =>
  typeof k === "string" && (BAR_LIST_KEYS as readonly string[]).includes(k);
const isStyleKey = (k: string): k is BarStyleKey => (BAR_STYLE_KEYS as readonly string[]).includes(k);
const isPrice = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Read the stored `settings.barSpiritLists` block (`null` / absent = every key
 * its default). Unknown keys and malformed values are DROPPED rather than
 * trusted: a stray `stylePrices.rocks: "5"` must read as unpriced, never as a
 * price nobody entered in that form. The retired keys
 * (`LOCATION_BAR_SPIRIT_LISTS_RETIRED_KEYS`) and the retired Double STYLE
 * price are dropped like any other unknown key — except that a valid
 * `standardPourOz` is the base pour while `basePourOz` is absent.
 */
export function barSpiritSettingsFromJSON(
  data: Record<string, unknown> | null | undefined,
): IBarSpiritSettings {
  const base = defaultBarSpiritSettings();
  if (!data) return base;

  // The old Mixer list switch is retired (2026-10-03): its options now live
  // on the Style group, by KIND. `'mixer'` is never an enabled list again;
  // a block that still says it — never saved since — offers all three kinds.
  const storedEnabled = Array.isArray(data.enabled) ? [...new Set(data.enabled.filter(isListKey))] : [];
  const enabled = storedEnabled.filter((k) => k !== "mixer");
  const mixerKinds: BarMixerKinds = {};
  if (isPlainObject(data.mixerKinds)) {
    for (const k of BAR_MIXER_KINDS) if (data.mixerKinds[k] === true) mixerKinds[k] = true;
  } else if (storedEnabled.includes("mixer")) {
    for (const k of BAR_MIXER_KINDS) mixerKinds[k] = true;
  }

  const stylePrices: Partial<Record<BarStyleKey, number>> = {};
  if (isPlainObject(data.stylePrices)) {
    for (const [k, v] of Object.entries(data.stylePrices)) {
      if (isStyleKey(k) && isPrice(v)) stylePrices[k] = v;
    }
  }

  const stylePours: Partial<Record<BarStyleKey, number>> = {};
  if (isPlainObject(data.stylePours)) {
    for (const [k, v] of Object.entries(data.stylePours)) {
      if (isStyleKey(k) && isValidBarPourOz(v)) stylePours[k] = v;
    }
  }

  const basePourOz = isValidBarPourOz(data.basePourOz)
    ? data.basePourOz
    : isValidBarPourOz(data.standardPourOz)
      ? data.standardPourOz
      : base.basePourOz;

  const doubleMultiplier = isValidBarDoubleMultiplier(data.doubleMultiplier)
    ? data.doubleMultiplier
    : base.doubleMultiplier;

  const houseRoles: Partial<Record<BarSpiritType, string>> = {};
  if (isPlainObject(data.houseRoles)) {
    for (const [k, v] of Object.entries(data.houseRoles)) {
      if (!(BAR_SPIRIT_TYPES as readonly string[]).includes(k) || typeof v !== "string" || v.length === 0) continue;
      // A "same as" record must name ANOTHER real type; anything else under
      // the prefix is dropped (the type reads as never set).
      if (v.startsWith(BAR_HOUSE_SAME_AS_PREFIX)) {
        const target = barHouseSameAsOf(v);
        if (!target || target === k) continue;
      }
      houseRoles[k as BarSpiritType] = v;
    }
  }

  const styleInstructions: Partial<Record<BarStyleInstructionKey, string>> = {};
  if (isPlainObject(data.styleInstructions)) {
    for (const [k, v] of Object.entries(data.styleInstructions)) {
      if ((BAR_STYLE_INSTRUCTION_KEYS as readonly string[]).includes(k) && isValidBarStyleInstruction(v)) {
        styleInstructions[k as BarStyleInstructionKey] = v.trim();
      }
    }
  }

  return {
    enabled,
    stylePrices,
    ...(isValidBarOptionPrice(data.doublePrice) ? { doublePrice: data.doublePrice } : {}),
    stylePours,
    basePourOz,
    doubleMultiplier,
    ...(Object.keys(houseRoles).length > 0 ? { houseRoles } : {}),
    ...(Object.keys(styleInstructions).length > 0 ? { styleInstructions } : {}),
    ...(Object.keys(mixerKinds).length > 0 ? { mixerKinds } : {}),
  };
}

/**
 * The Double STYLE price a block still stores from before bar v2
 * (`stylePrices.double` — House of Pho's $18, a full drink price on a style),
 * or `undefined`. For the "Double is now an upcharge on the style" notice
 * ONLY: it is never read as a price, and never converted into `doublePrice`
 * (OrderUp never sets a price).
 */
export function barRetiredDoubleStylePrice(block: unknown): number | undefined {
  if (!isPlainObject(block) || !isPlainObject(block.stylePrices)) return undefined;
  const v = block.stylePrices.double;
  return isPrice(v) ? v : undefined;
}

/**
 * The STORED block (`settings.barSpiritLists`) as the restaurant model carries
 * it: every key normalised through `barSpiritSettingsFromJSON` — so the retired
 * keys and any malformed value never ride out on the model — while staying
 * SPARSE: only keys the doc carries appear, none is defaulted in (a stored
 * `standardPourOz` appears as `basePourOz`). Total: an old or malformed shape
 * never throws. An absent (or non-object) block stays `undefined`.
 */
export function barSpiritListsBlockFromJSON(data: unknown): LocationBarSpiritListsBlock | undefined {
  if (!isPlainObject(data)) return undefined;
  const normalised = barSpiritSettingsFromJSON(data);
  const block: { -readonly [K in LocationBarSpiritListsKey]?: IBarSpiritSettings[K] } = {};
  for (const key of LOCATION_BAR_SPIRIT_LISTS_KEYS) {
    const stored =
      key in data ||
      (key === "basePourOz" && "standardPourOz" in data) ||
      (key === "mixerKinds" && Array.isArray(data.enabled) && data.enabled.includes("mixer"));
    if (stored && normalised[key] !== undefined) (block as Record<string, unknown>)[key] = normalised[key];
  }
  return block;
}

/**
 * What one style pours relative to the base: `stylePour / basePour`, 1 for a
 * style with no pour of its own. Every spirit draw of the line is multiplied
 * by it (and by `doubleMultiplier` when Double is picked).
 */
/** A multiplier as a person reads it: 4.5, 4.17, 2 — at most two decimals. */
const formatMultiplier = (m: number): string => String(Math.round(m * 100) / 100);

/**
 * Every style whose pour is more than `BAR_POUR_MULTIPLIER_MAX` times the base
 * pour — a multiplier order close refuses — with an actionable message naming
 * the style. Checked on the WHOLE settings a save leaves, so lowering only the
 * base pour past an existing style's pour is caught too. `[]` = all within.
 */
export function barStylePourCapProblems(
  settings: Pick<IBarSpiritSettings, "basePourOz"> & Partial<Pick<IBarSpiritSettings, "stylePours">>,
): Array<{ readonly style: BarStyleKey; readonly ratio: number; readonly message: string }> {
  const out: Array<{ style: BarStyleKey; ratio: number; message: string }> = [];
  for (const { key, label } of BAR_STYLE_OPTIONS) {
    if (settings.stylePours?.[key] === undefined) continue;
    const ratio = barStylePourMultiplier(settings, key);
    if (ratio > BAR_POUR_MULTIPLIER_MAX) {
      out.push({
        style: key,
        ratio,
        message: `${label} pours ${formatMultiplier(ratio)}× the base pour; the most is ${BAR_POUR_MULTIPLIER_MAX}× — raise the base pour or lower ${label}.`,
      });
    }
  }
  return out;
}

/**
 * The how-to a style (or Double) shows at the till: the location's own text
 * when it rewrote it, else OrderUp's standard — and which of the two it is,
 * so the settings can say so and offer "Reset to OrderUp standard".
 */
export function barStyleInstructionsFor(
  settings: Partial<Pick<IBarSpiritSettings, "styleInstructions">>,
  key: BarStyleInstructionKey,
): { readonly text: string; readonly standard: boolean } {
  const own = settings.styleInstructions?.[key];
  return own ? { text: own, standard: false } : { text: BAR_STYLE_STANDARD_INSTRUCTIONS[key], standard: true };
}

export function barStylePourMultiplier(
  settings: Pick<IBarSpiritSettings, "basePourOz"> & Partial<Pick<IBarSpiritSettings, "stylePours">>,
  style: BarStyleKey,
): number {
  return (settings.stylePours?.[style] ?? settings.basePourOz) / settings.basePourOz;
}

// ── Provenance stamped on the generated documents ───────────────────────────

/**
 * On a generated modifier GROUP. `required` and `order` are the generator's
 * last-written baseline, so a restaurant's own later edit is distinguishable
 * from ours: a group the restaurant re-ordered keeps its order for good
 * (`orderedByRestaurant`), and a requirement changed in the group editor is
 * kept until the restaurant changes the list's setting again.
 */
export interface IBarListGroupProvenance {
  readonly key: BarListKey;
  readonly generatedAt: Date;
  /** `true` on a spirit list's REQUIRED group (mixed-drink items); absent on the optional one. */
  readonly required?: boolean;
  /** Generated option `modifierId`s in the order the generator last wrote them. */
  readonly order?: number[];
  /** The restaurant re-ordered the options; the generator appends and never re-sorts. */
  readonly orderedByRestaurant?: boolean;
  /**
   * When the generator wrote `includePosPrompt: true` on this group — a
   * spirit call list only (`barListGroupPromptsOnPos`, owner 2026-10-09). Set
   * at creation, or ONCE on a call list created before that decision; a group
   * carrying it is never prompted again, so a restaurant that switched the
   * prompt off in the group editor keeps it off.
   */
  readonly posPromptSeededAt?: Date;
}

/** On a generated modifier (one option). `barBottleGuid` sits beside it at the top level. */
export interface IBarListOptionProvenance {
  readonly listKey: BarListKey;
  /** The carried inventory item this option pours (spirit / mixer options). */
  readonly inventoryGuid?: string;
  /** A Style option's style — or a retired one (`BAR_RETIRED_STYLE_KEYS`) on an option archived since. */
  readonly styleKey?: BarStyleKey | BarRetiredStyleKey;
  /**
   * What the generator last wrote to this option's `modifier_ingredients`
   * doc. Re-written only when this changes, so a draw the restaurant
   * adjusted in RecipeOps survives an unrelated regeneration.
   */
  readonly drawBasis?: string;
  /**
   * The price the generator last wrote from a SETTINGS price — a Style
   * option's `stylePrices` entry, or the Double option's `doublePrice`. While the option's `price` still
   * equals it, the restaurant has not priced the option itself, so a style
   * price it enters later reaches the option. Once the restaurant sets its
   * own price the two differ and the generator never touches it again.
   *
   * On a spirit or mixer option it is RETIRED: under tier pricing (before
   * 2026-09-29) it marked a price copied from a tier. Regeneration migrates
   * such an option — the price becomes the restaurant's own at the same value
   * and this marker is deleted (`BarSpiritListService`, House of Pho's $2
   * vodkas) — and nothing writes it on those lists again.
   */
  readonly writtenPrice?: number;
  /**
   * The option is OFF because nothing priced it: a spirit / mixer option the
   * restaurant has not priced on the Spirit lists tab (or whose price it
   * cleared), a style whose price was not entered. It goes on once the
   * restaurant prices it. An option the restaurant switched off itself never
   * carries this, and is never switched on by OrderUp.
   */
  readonly heldUnpriced?: boolean;
  /**
   * Where the option sorts in its list: `sortRank` (a style's fixed place; 0
   * on every other list) then its name — the name the restaurant gave the
   * option, else `sortName`, the generator's. Stored so the group's order is
   * derived from the options themselves, inside the membership transaction,
   * never from one caller's possibly stale plan.
   */
  readonly sortRank?: number;
  readonly sortName?: string;
  /**
   * A MIXER option on the Style group (2026-10-03): the kind of mixer it is
   * (`gun` / `house` / `packaged`). Its `inventoryGuid` is the carried mixer.
   */
  readonly mixerKind?: BarMixerDispense;
  /**
   * A mixer option's UPCHARGE over the Mixed price — the restaurant's own
   * price box on the Fountain / House-made / Packaged tab ("Upcharge
   * (optional)"). Absent = no upcharge: the mixer rings the Mixed price. Never
   * written by OrderUp except to carry over the price the restaurant had typed
   * on the same mixer's retired Mixer-list option.
   */
  readonly upcharge?: number;
  /**
   * The till name / chit name OrderUp last wrote onto this option from its
   * row (`inventory_items.barPosName` / `barChitName`, design 2026-10-05 Part
   * 2). Only a name equal to it is removed when the row is cleared — a name
   * the restaurant typed in Modifier Groups is its own (`barOptionNamePatch`).
   */
  readonly writtenPosName?: string;
  readonly writtenKitchenName?: string;
  /**
   * The photo OrderUp last wrote onto this option from its row (design
   * 2026-10-05 Part 1, `barOptionImagePatch`). Only a photo equal to it is
   * removed when the row resolves none — a photo the restaurant set on the
   * modifier itself is its own.
   */
  readonly writtenImageUrl?: string;
}

// ── Per-item pricing: what an option's price and state ARE ────────────────

/** A generated option as it rings: on sale, held off for want of a price, or switched off by the restaurant. */
export type BarListOptionState = "on" | "off_unpriced" | "off_by_restaurant";

/** The stored fields of a generated option that decide its price and state. */
export interface BarListOptionPriceFacts {
  readonly listKey: BarListKey;
  /** The modifier's own `price` — absent = no price at all (never read as $0). */
  readonly price?: number;
  /** `isActive` absent reads as ON, as the POS reads it. */
  readonly isActive?: boolean;
  readonly isArchived?: boolean;
  readonly heldUnpriced?: boolean;
  readonly writtenPrice?: number;
}

/**
 * Does an EMPTY price box on this list's option mean "on at no upcharge"?
 * THE one rule for it (owner, 2026-10-06 — the mixer rule: an empty mixer
 * upcharge is no upcharge): a SPIRIT / LIQUEUR call brand with no entered
 * price is LIVE at a $0.00 upcharge, so the drink rings its Style price only;
 * an entered amount is the upcharge. A brand goes off only by its own off
 * switch or by not being carried. Style and Double keep "unpriced = off": a
 * style's price IS the drink's price, and Double is off until its upcharge is
 * entered. The retired Mixer list keeps its old rule.
 *
 * Read by the generator's held/live decision, the option-price save, the
 * Spirit lists preview (`barListOptionPriceView`), `upgradePending`, the
 * Firestore rule (`heldBarOptionStaysUnsold` mirrors it by list key) and the
 * Spirit lists tab.
 */
export function barEmptyPriceIsNoUpcharge(listKey: BarListKey): boolean {
  return isBarSpiritListKey(listKey);
}

/**
 * The retired TIER marker on a spirit / mixer option, resolved (tier pricing
 * ended 2026-09-29). Under tier pricing an option's `price` was copied from
 * `tierPrices` / `mixerPrice` and `barOption.writtenPrice` recorded the copy.
 * Per item, the price on the option IS the restaurant's price, so:
 *
 * - a live option priced from a tier (`price === writtenPrice`) KEEPS that
 *   price as its own — House of Pho's two vodkas at the call tier's $2.00 stay
 *   live at $2.00;
 * - an option HELD off whose price still equals the tier price it was given
 *   (its tier price was removed later) loses that stale price — nobody chose
 *   it for this option, so it stays off, unpriced;
 * - either way the marker is deleted, so the option is never read as
 *   tier-priced again.
 *
 * A Style or Double option keeps `writtenPrice`: its settings price
 * (`stylePrices` / `doublePrice`) still prices it.
 * Pure and idempotent — `null` once there is nothing left to migrate.
 */
export function barRetiredTierPriceMigration(
  o: BarListOptionPriceFacts,
): { readonly dropPrice: boolean } | null {
  if (isBarSettingsPricedListKey(o.listKey) || o.writtenPrice === undefined) return null;
  const held = o.heldUnpriced === true && o.isActive !== true;
  return { dropPrice: held && o.price === o.writtenPrice };
}

/**
 * The price an option rings at and its state — what the Spirit lists tab
 * shows on each row, and what "has a priced option" counts. Reads THROUGH
 * the retired-tier migration, so an option not yet regenerated reports what
 * regeneration will leave.
 *
 * - `on`: on sale at `price`.
 * - `off_unpriced`: held off until the restaurant prices it. Its `price` is
 *   `null` even when a price was typed on it in the modifier editor while it
 *   stayed off — that price is not in force.
 * - `off_by_restaurant`: priced or not, the restaurant switched it off in
 *   Modifier Groups; OrderUp never switches it back on.
 *
 * An ARCHIVED option (a bottle carried again, not yet regenerated) reports
 * the state its restoration will give it.
 */
export function barListOptionPriceView(
  o: BarListOptionPriceFacts,
): { readonly price: number | null; readonly state: BarListOptionState } {
  const migration = barRetiredTierPriceMigration(o);
  const price = typeof o.price === "number" && !migration?.dropPrice ? o.price : null;
  const held = o.heldUnpriced === true;
  if (barEmptyPriceIsNoUpcharge(o.listKey)) {
    // A call brand: an empty box is "no upcharge", never "off". A price typed
    // on a (legacy) held option was never in force, so it reads as none.
    const inForce = held && o.isActive !== true ? 0 : (price ?? 0);
    if (o.isArchived !== true && !held && o.isActive === false) return { price: inForce, state: "off_by_restaurant" };
    return { price: inForce, state: "on" };
  }
  if (o.isArchived === true) {
    return !held && price !== null ? { price, state: "on" } : { price: null, state: "off_unpriced" };
  }
  if (held && o.isActive !== true) return { price: null, state: "off_unpriced" };
  if (o.isActive === false) return { price, state: "off_by_restaurant" };
  return { price, state: "on" };
}

/**
 * A spirit or mixer option's price as the restaurant enters it on the Spirit
 * lists tab: finite, 0 to 1000, whole cents. `0` is an entered price (free).
 */
export const BAR_OPTION_PRICE_MAX = 1000;
export function isValidBarOptionPrice(v: unknown): v is number {
  return (
    typeof v === "number" &&
    Number.isFinite(v) &&
    v >= 0 &&
    v <= BAR_OPTION_PRICE_MAX &&
    Math.abs(v * 100 - Math.round(v * 100)) < 1e-6
  );
}

/**
 * A bar price box's text, read by the same rule as `isValidBarOptionPrice`:
 * `null` when empty (NOT 0 — an empty box is "not priced", 0 is "no
 * charge"), the number when it is a price, `undefined` when it is not. A
 * value finer than a cent is refused, never rounded: the restaurant sees why
 * and types the price it means. The one parser behind every bar price box
 * (Spirit lists, the mixer tabs, per-menu prices).
 */
export function parseBarPriceInput(text: string | undefined): number | null | undefined {
  const t = (text ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  return isValidBarOptionPrice(n) ? n : undefined;
}

/** Is this box's only problem that it is finer than a cent ("2.005")? Drives the "whole cents only" hint. */
export function isBarPriceFinerThanACent(text: string | undefined): boolean {
  const t = (text ?? "").trim();
  if (t === "") return false;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && !isValidBarOptionPrice(n) && isValidBarOptionPrice(Math.round(n * 100) / 100);
}

/** A saved bar price as a box's text: "" when there is none — never a prefilled 0. */
export function barPriceText(v: number | null | undefined): string {
  return v === undefined || v === null ? "" : String(v);
}

/**
 * A bar price as the Bar page SAYS it ("$6.00", "$0.50") — in toasts, summaries
 * and pickers alike, so one price never reads two ways on one page. Bar prices
 * are whole cents (`isValidBarOptionPrice`), so two places is exact.
 */
export function barMoneyText(v: number): string {
  return `$${v.toFixed(2)}`;
}

/** Compare two options by their stored sort position (a style's place, then name, then a stable id). */
export function compareBarListOptions(
  a: { sortRank?: number; sortName?: string; tieBreak: string },
  b: { sortRank?: number; sortName?: string; tieBreak: string },
): number {
  const rank = (a.sortRank ?? 0) - (b.sortRank ?? 0);
  if (rank !== 0) return rank;
  const name = (a.sortName ?? "").localeCompare(b.sortName ?? "", undefined, { sensitivity: "base" });
  return name !== 0 ? name : a.tieBreak.localeCompare(b.tieBreak);
}

// ── Planning ────────────────────────────────────────────────────────────────

/** A carried inventory item as the list planner reads it. */
export interface SpiritListCarriedItem {
  readonly inventoryGuid: string;
  /** The catalog row, when carried from the catalog; `null` for the restaurant's own bottle. */
  readonly bottle: IBarBottle | null;
  /** The inventory item's own name — what an own bottle's option is called. */
  readonly name?: string;
  readonly barSpiritType?: BarSpiritType;
  readonly trackDepletion?: boolean;
  /** An own bottle's dispense (a catalog mixer's comes from `bottle.dispense`). */
  readonly barMixerDispense?: BarMixerDispense;
  /** The inventory item's stock unit — a draw's unit must equal it (Decision 7). */
  readonly unit?: string;
  /** Pour units in one container (`barBottleContainerSize`). */
  readonly containerSize?: number;
  /** The size on the shelf, when it differs from the catalog row's. */
  readonly bottleSizeMl?: number;
  /**
   * The platform role this item holds (`inventory_items.platformIngredientGuid`)
   * — held ONLY by a role's house pour (one bottle per role,
   * `assertHousePourFree`). What makes a carried spirit its type's house
   * bottle (`barHousePourFor`).
   */
  readonly platformIngredientGuid?: string;
  /**
   * A MIXER's role category — of the role it plays or is poured as
   * (`IngredientCategory`: `MIXER` for a soda or a juice, `SWEETENER` for a
   * syrup, …). Decides whether the mixer TOPS a drink and so may be a Style
   * choice (`isBarToppingMixer`). Absent when it has no role or the role could
   * not be read.
   */
  readonly mixerRoleCategory?: string;
  /**
   * The row's own till and chit names (`inventory_items.barPosName` /
   * `barChitName`, design 2026-10-05 Part 2), trimmed; absent = none. Every
   * option built from the row carries them as `posName` / `kitchenName`.
   */
  readonly posName?: string;
  readonly chitName?: string;
  /**
   * The row's OWN photo (`inventory_items.imageUrl`, design 2026-10-05 Part
   * 1). The option carries `resolveBarBottleImage` of it and `bottle.imageUrl`
   * — the restaurant's photo, else the catalog's.
   */
  readonly imageUrl?: string;
}

/** What one option draws per selection. `null` = draws nothing. */
export interface SpiritListOptionDraw {
  readonly quantity: number;
  readonly unit: "oz" | "ea";
}

export interface SpiritListOptionPlan {
  readonly barBottleGuid: string | null;
  readonly inventoryGuid: string | null;
  readonly name: string;
  /**
   * The price the SETTINGS give this option — a Style option's `stylePrices`
   * entry, the Double option's `doublePrice`. Always `null` on a spirit,
   * liqueur or mixer option: those are
   * priced one at a time on the option itself, never by the planner. `null` =
   * none entered here: a new option is created OFF, never sold at a price
   * nobody chose.
   */
  readonly price: number | null;
  /**
   * A Style or Double option's how-to for the till (`barStyleInstructionsFor`)
   * — written to the option's `posDescription`. Absent on every other list.
   */
  readonly instructions?: string;
  /** See `IBarListOptionProvenance.sortRank` / `sortName`. */
  readonly sortRank: number;
  readonly sortName: string;
  readonly styleKey?: BarStyleKey;
  readonly draw: SpiritListOptionDraw | null;
  /** A brand pick REPLACES the line's recipe pour of this spirit (only with a draw). */
  readonly substitutes?: { readonly spiritType: BarSpiritType };
  readonly pourMultiplier?: number;
  /** Fingerprint of `draw` + `substitutes` + `pourMultiplier`; see `IBarListOptionProvenance.drawBasis`. */
  readonly drawBasis: string;
  /**
   * The Style group's MIXER option (2026-10-03): the mixer's kind. Its `price`
   * is the Mixed price (`stylePrices.tall`) BEFORE the mixer's own upcharge,
   * which lives on the stored option (`IBarListOptionProvenance.upcharge`) —
   * the service rings `barMixerStylePrice(price, upcharge)`.
   */
  readonly mixerKind?: BarMixerDispense;
  /**
   * The archived `bar_style_tall` option: kept as the carrier of the per-menu
   * Mixed price, never a member of a group (`BAR_MIXED_STYLE_KEY`).
   */
  readonly mixedCarrier?: true;
  /**
   * The row's till name and chit name, written to the option's `posName` /
   * `kitchenName` (`barOptionNamePatch`). Absent when the row has none — the
   * till then shows `name` and the chit prints `name`, exactly as before.
   */
  readonly posName?: string;
  readonly kitchenName?: string;
  /**
   * The row's resolved photo (`resolveBarBottleImage`), written to the
   * option's `imageUrl` (`barOptionImagePatch`). Absent when neither the row
   * nor its catalog bottle has one.
   */
  readonly imageUrl?: string;
}

/** The option names — and the photo — a carried row gives every option built from it. */
function rowNamesOf(c: SpiritListCarriedItem): { posName?: string; kitchenName?: string; imageUrl?: string } {
  const posName = normalizeBarRowName(c.posName);
  const kitchenName = normalizeBarRowName(c.chitName);
  const image = resolveBarBottleImage({ restaurantImageUrl: c.imageUrl, catalogImageUrl: c.bottle?.imageUrl });
  return {
    ...(posName ? { posName } : {}),
    ...(kitchenName ? { kitchenName } : {}),
    ...(image ? { imageUrl: image.url } : {}),
  };
}

export interface SpiritListPlan {
  readonly options: SpiritListOptionPlan[];
  /**
   * Spirit options that pour nothing because their bottle is not stocked in
   * ounces (a liqueur counted in dashes): the pick is still rung, and the
   * drink's recipe pours the house bottle as it would with nothing picked.
   */
  readonly unmeasured: string[];
}

function isMixer(item: SpiritListCarriedItem): boolean {
  if (item.bottle) return item.bottle.category === "mixer";
  return !!item.barMixerDispense;
}

function mixerDraw(item: SpiritListCarriedItem): SpiritListOptionDraw | null {
  if (item.trackDepletion === false) return null;
  if (item.unit === "ea") return { quantity: 1, unit: "ea" };
  if (item.unit === "oz") {
    const dispense = item.bottle?.dispense ?? item.barMixerDispense;
    // A packaged mixer poured by the ounce (it plays or is poured as a role):
    // one serving is one container. Gun, house-made, or a container of unknown
    // size: a standard top.
    const perContainer = item.containerSize;
    if (dispense === "packaged" && typeof perContainer === "number" && perContainer > 0) {
      return { quantity: perContainer, unit: "oz" };
    }
    return { quantity: BAR_MIXER_TOP_OZ, unit: "oz" };
  }
  return null;
}

/**
 * How each carried item is named on a list: its catalog name (or its own name
 * for an own bottle), with its size when two carried items would otherwise
 * read the same on a till.
 */
function listOptionNames(members: ReadonlyArray<SpiritListCarriedItem>): (c: SpiritListCarriedItem) => string {
  const baseName = (c: SpiritListCarriedItem) =>
    c.bottle ? barBottleDisplayName(c.bottle) : (c.name ?? "").trim() || "Unnamed bottle";
  const nameCounts = new Map<string, number>();
  for (const c of members) {
    const n = baseName(c).toLowerCase();
    nameCounts.set(n, (nameCounts.get(n) ?? 0) + 1);
  }
  return (c) => {
    const n = baseName(c);
    if ((nameCounts.get(n.toLowerCase()) ?? 0) < 2) return n;
    const size = c.bottleSizeMl ?? c.bottle?.sizeMl;
    return size ? `${n} ${formatBarBottleSize(size)}` : n;
  };
}

function drawBasisOf(
  draw: SpiritListOptionDraw | null,
  substitutes: BarSpiritType | undefined,
  pourMultiplier: number | undefined,
): string {
  const parts = [draw ? `${draw.quantity}${draw.unit}` : "none"];
  if (substitutes) parts.push(`sub:${substitutes}`);
  if (pourMultiplier !== undefined) parts.push(`x${pourMultiplier}`);
  return parts.join("|");
}

/** The settings a plan reads — what pours, and the settings prices of Style and Double. */
export type SpiritListPlanSettings = Pick<IBarSpiritSettings, "basePourOz"> &
  Partial<
    Pick<
      IBarSpiritSettings,
      "stylePrices" | "stylePours" | "doublePrice" | "doubleMultiplier" | "styleInstructions" | "mixerKinds"
    >
  >;

/**
 * Does this carried mixer TOP a drink, so a Style choice may name it ("Vodka,
 * Club Soda")? A style choice is what the glass is filled with, never a
 * sweetener or a dash. The catalog's role categories say which is which:
 * - a role in `MIXER` (sodas, juices, tonic, energy drinks) — yes;
 * - any other role category (`SWEETENER` — simple syrup, agave, orgeat;
 *   `BITTERS`; `FRUIT`; …) — no;
 * - no role at all: a gun soda or a packaged can tops a drink by nature — yes;
 *   a role-less house-made mix (sour mix, grenadine) — no, since nothing says
 *   it is a topper.
 */
export function isBarToppingMixer(item: Pick<SpiritListCarriedItem, "bottle" | "barMixerDispense" | "mixerRoleCategory">): boolean {
  if (item.mixerRoleCategory !== undefined) return item.mixerRoleCategory.toUpperCase() === "MIXER";
  const dispense = item.bottle?.dispense ?? item.barMixerDispense;
  return dispense === "gun" || dispense === "packaged";
}

/** A carried mixer's kind (`dispense`) — from its catalog row, else the own bottle's. */
export function barMixerKindOf(item: Pick<SpiritListCarriedItem, "bottle" | "barMixerDispense">): BarMixerDispense | undefined {
  return item.bottle?.dispense ?? item.barMixerDispense;
}

/**
 * What a mixer choice on the Style group rings: the Mixed price plus the
 * mixer's own upcharge (absent = none — an empty upcharge never turns the
 * mixer off). `null` while the Mixed price is not entered: the option is held
 * off, exactly like an unpriced style. Whole cents. OrderUp never fills either.
 */
export function barMixerStylePrice(mixedPrice: number | null | undefined, upcharge: number | null | undefined): number | null {
  if (typeof mixedPrice !== "number") return null;
  return Math.round((mixedPrice + (typeof upcharge === "number" ? upcharge : 0)) * 100) / 100;
}

/**
 * How a mixer choice is built, at the till: the Mixed how-to with the mixer
 * named. OrderUp's standard ends "top with the mixer." — that becomes "top
 * with Club Soda."; a location's own Mixed text is kept whole, with the mixer
 * appended ("… Mixer: Club Soda.").
 */
export function barMixerStyleInstructions(
  settings: Partial<Pick<IBarSpiritSettings, "styleInstructions">>,
  mixerName: string,
): string {
  const { text, standard } = barStyleInstructionsFor(settings, BAR_MIXED_STYLE_KEY);
  const name = mixerName.trim();
  if (standard && text.endsWith("the mixer.")) return `${text.slice(0, -"the mixer.".length)}${name}.`;
  const own = text.trim();
  return `${own}${/[.!?]$/.test(own) ? "" : "."} Mixer: ${name}.`;
}

/** Where the mixer choices sort on the Style group: after every named style. */
export const BAR_MIXER_STYLE_SORT_RANK = 10;

/**
 * The per-menu prices a menu's Style references must carry for the mixer
 * choices (B1/B4, 2026-10-03), as edits `{ modifierId → price | null }` — only
 * those that differ from `current`.
 *
 * The ROOT→LEAF resolvers (`resolveParentPriceOverrides` on the till,
 * `resolveModifierPriceOverrides` online and in the assistant) take a list's
 * WHOLE price set from the first menu that prices any of it, and ring an
 * option that menu does not price at its All menus price. So a menu with its
 * own Mixed price (`mixedPrice`, the override on the `bar_style_tall`
 * carrier) gives EVERY live mixer choice an explicit price there — that menu's
 * Mixed price plus the mixer's upcharge — and a menu without one carries none
 * (the All menus price, Mixed + upcharge, is right). A retired mixer choice
 * never keeps a price.
 */
export function barMixerMenuPriceEdits(input: {
  /** The menu's Mixed price (its override on the carrier), or `null` when it has none. */
  readonly mixedPrice: number | null;
  /** Every mixer choice ever generated: its modifierId, upcharge, and whether it is live. */
  readonly mixers: ReadonlyArray<{ readonly modifierId: number; readonly upcharge?: number; readonly live: boolean }>;
  /** The menu's current overrides on the Style reference, by modifierId. */
  readonly current: ReadonlyMap<number, number>;
}): Map<number, number | null> {
  const edits = new Map<number, number | null>();
  for (const m of input.mixers) {
    const want = m.live ? barMixerStylePrice(input.mixedPrice, m.upcharge) : null;
    const have = input.current.get(m.modifierId) ?? null;
    if (want !== have) edits.set(m.modifierId, want);
  }
  return edits;
}

/**
 * What one list contains, in order, with each option's price and draw.
 *
 * - A spirit list is the CALL list: the carried items whose `barSpiritType`
 *   is that key, by name, EXCEPT the house bottle (`houseInventoryGuid`) —
 *   no pick pours it. A catalog bottle's tier plays no part.
 * - The (retired) Mixer list holds carried catalog mixers and own bottles
 *   carrying a dispense, by name — only stored options are still read.
 * - Neither prices an option (`price: null`): the restaurant prices each one
 *   on the option itself.
 * - The Style list is Rocks, Neat, Up, Shot, priced from `stylePrices` (a
 *   style with its own pour multiplies by `barStylePourMultiplier`), then
 *   one MIXER choice per carried topping mixer of an offered kind
 *   (`mixerKinds`, `isBarToppingMixer`): it rings the Mixed price
 *   (`stylePrices.tall`, plus the stored upcharge), pours the Mixed pour and
 *   draws its mixer. Tall itself is planned only as the archived carrier of
 *   the per-menu Mixed price (`mixedCarrier`).
 * - The Double list is one option, "Double", priced from `doublePrice` (the
 *   upcharge) and pouring `doubleMultiplier`.
 *
 * Two carried sizes of one product would read identically on a till, so both
 * are named with their size.
 */
export function planSpiritList(
  key: BarListKey,
  carried: ReadonlyArray<SpiritListCarriedItem>,
  settings: SpiritListPlanSettings,
  house: { readonly houseInventoryGuid?: string } = {},
): SpiritListPlan {
  if (key === "style") {
    const styles = BAR_STYLE_OPTIONS.map((s, i): SpiritListOptionPlan => {
      const m = barStylePourMultiplier(settings, s.key);
      const pourMultiplier = m === 1 ? undefined : m;
      return {
        barBottleGuid: null,
        inventoryGuid: null,
        name: s.label,
        price: settings.stylePrices?.[s.key] ?? null,
        instructions: barStyleInstructionsFor(settings, s.key).text,
        sortRank: i,
        sortName: s.label,
        styleKey: s.key,
        draw: null,
        ...(pourMultiplier !== undefined ? { pourMultiplier } : {}),
        drawBasis: drawBasisOf(null, undefined, pourMultiplier),
        ...(s.key === BAR_MIXED_STYLE_KEY ? { mixedCarrier: true as const } : {}),
      };
    });
    // The mixer choices (2026-10-03): one per carried TOPPING mixer of a kind
    // the location offers, named like the mixer, ringing the Mixed price and
    // pouring the Mixed pour — the tall drink, named by what tops it.
    const mixedPrice = settings.stylePrices?.[BAR_MIXED_STYLE_KEY] ?? null;
    const mixedMultiplier = barStylePourMultiplier(settings, BAR_MIXED_STYLE_KEY);
    const pourMultiplier = mixedMultiplier === 1 ? undefined : mixedMultiplier;
    const mixers = carried.filter((c) => {
      if (!isMixer(c) || !isBarToppingMixer(c)) return false;
      const kind = barMixerKindOf(c);
      return !!kind && settings.mixerKinds?.[kind] === true;
    });
    const names = listOptionNames(mixers);
    const mixerOptions = [...mixers]
      .sort((a, b) => {
        const byName = names(a).localeCompare(names(b), undefined, { sensitivity: "base" });
        return byName !== 0 ? byName : a.inventoryGuid.localeCompare(b.inventoryGuid);
      })
      .map((c): SpiritListOptionPlan => {
        const name = names(c);
        const draw = mixerDraw(c);
        return {
          barBottleGuid: c.bottle?.guid ?? null,
          inventoryGuid: c.inventoryGuid,
          name,
          price: mixedPrice,
          instructions: barMixerStyleInstructions(settings, name),
          sortRank: BAR_MIXER_STYLE_SORT_RANK,
          sortName: name,
          draw,
          ...(pourMultiplier !== undefined ? { pourMultiplier } : {}),
          drawBasis: drawBasisOf(draw, undefined, pourMultiplier),
          mixerKind: barMixerKindOf(c)!,
          ...rowNamesOf(c),
        };
      });
    return { options: [...styles, ...mixerOptions], unmeasured: [] };
  }

  if (key === "double") {
    const pourMultiplier = settings.doubleMultiplier ?? BAR_DOUBLE_MULTIPLIER_DEFAULT;
    return {
      options: [
        {
          barBottleGuid: null,
          inventoryGuid: null,
          name: BAR_DOUBLE_OPTION_LABEL,
          price: settings.doublePrice ?? null,
          instructions: barStyleInstructionsFor(settings, "double").text,
          sortRank: 0,
          sortName: BAR_DOUBLE_OPTION_LABEL,
          draw: null,
          pourMultiplier,
          drawBasis: drawBasisOf(null, undefined, pourMultiplier),
        },
      ],
      unmeasured: [],
    };
  }

  const members = carried.filter((c) =>
    key === "mixer"
      ? isMixer(c)
      : c.barSpiritType === key && !isMixer(c) && c.inventoryGuid !== house.houseInventoryGuid,
  );

  const optionName = listOptionNames(members);

  const sorted = [...members].sort((a, b) => {
    const byName = optionName(a).localeCompare(optionName(b), undefined, { sensitivity: "base" });
    return byName !== 0 ? byName : a.inventoryGuid.localeCompare(b.inventoryGuid);
  });

  const unmeasured: string[] = [];
  const options = sorted.map((c): SpiritListOptionPlan => {
    const name = optionName(c);
    if (key === "mixer") {
      const draw = mixerDraw(c);
      return {
        barBottleGuid: c.bottle?.guid ?? null,
        inventoryGuid: c.inventoryGuid,
        name,
        price: null,
        sortRank: 0,
        sortName: name,
        draw,
        drawBasis: drawBasisOf(draw, undefined, undefined),
        ...rowNamesOf(c),
      };
    }
    const draw: SpiritListOptionDraw | null =
      c.unit === "oz" && c.trackDepletion !== false
        ? { quantity: settings.basePourOz, unit: "oz" }
        : null;
    if (!draw) unmeasured.push(name);
    return {
      barBottleGuid: c.bottle?.guid ?? null,
      inventoryGuid: c.inventoryGuid,
      name,
      price: null,
      sortRank: 0,
      sortName: name,
      draw,
      ...(draw ? { substitutes: { spiritType: key } } : {}),
      drawBasis: drawBasisOf(draw, draw ? key : undefined, undefined),
      ...rowNamesOf(c),
    };
  });

  return { options, unmeasured };
}

/** The settings that decide whether a list is on: the toggles, and the style prices. */
export type BarListOnSettings = Pick<IBarSpiritSettings, "enabled" | "stylePrices">;

/**
 * Has the restaurant priced at least one style for all menus (`stylePrices`)?
 * A free style (0) is a price. The retired Double STYLE price is not a style
 * (`barSpiritSettingsFromJSON` drops it), so it prices nothing here.
 */
export function isBarStylePriced(settings: Pick<IBarSpiritSettings, "stylePrices">): boolean {
  return BAR_STYLE_KEYS.some((k) => typeof settings.stylePrices[k] === "number");
}

/**
 * Is this list ON for the restaurant? THE one rule every reader goes through —
 * regeneration, the conversion check (`upgradePending`), the list statuses and
 * the Spirit lists tab's switches.
 *
 * - A list is on when the restaurant turned it on.
 * - Style is ALSO on whenever a style is priced: a mixed drink's price IS its
 *   style price (design A2), so a mixed drink without Style rings $0.00.
 *   House of Pho priced every style at $12 without ever switching Style on,
 *   and its VODKA rang at $0.00 (2026-10-03). To turn Style off, clear the
 *   style prices.
 * - Double is on whenever Style is: Double is an upcharge on the style picked
 *   (design A2), so a bar that rings styles can always ring a Double. (It
 *   stays held off at the till until `doublePrice` is entered.)
 */
export function isBarListOn(settings: BarListOnSettings, key: BarListKey): boolean {
  // The Mixer list is retired: its options ride on the Style group (`mixerKinds`).
  if (key === "mixer") return false;
  if (settings.enabled.includes(key)) return true;
  if (key === "style") return isBarStylePriced(settings);
  if (key === "double") return isBarListOn(settings, "style");
  return false;
}

/**
 * The call lists an adopted cocktail carries ("Add lists to catalog cocktails"),
 * from the spirit types its recipe pours: one per spirit the drink is BUILT on,
 * so a Margarita offers its tequila and a Long Island its four spirits. A
 * liqueur list only for a drink built on liqueur alone (an Amaretto Sour): every
 * call list prompts on the till, and a Margarita asking for a triple-sec brand
 * after its tequila is a second prompt nobody asked for (owner, 2026-10-26: the
 * swap staff need is "sub the tequila"). Order follows `types`.
 */
export function barCocktailCallListKeys(types: Iterable<BarSpiritType>): BarSpiritType[] {
  const unique = [...new Set(types)];
  const spirits = unique.filter((t) => t !== "liqueur");
  return spirits.length > 0 ? spirits : unique;
}

/** Is ANY list on (`isBarListOn`)? A location with none has nothing to generate. */
export function isAnyBarListOn(settings: BarListOnSettings & Partial<Pick<IBarSpiritSettings, "mixerKinds">>): boolean {
  // A location with only mixer kinds on still has a retired Mixer group to
  // convert and mixer options to hold ready for its first style price.
  return BAR_LIST_KEYS.some((k) => isBarListOn(settings, k)) || isAnyBarMixerKindOn(settings);
}

/**
 * The HOUSE bottle of one spirit type (design A1): the carried, non-mixer item
 * of that type holding the type's HOUSE ROLE (`platformIngredientGuid`). A
 * mixed drink with no call pick pours it, and it is never an option on the
 * call list (`planSpiritList`'s `houseInventoryGuid`). `null` = no house
 * bottle set — a mixed-drink item of that type cannot be created.
 *
 * ONE truth: the role. A role is held by exactly one bottle
 * (`assertHousePourFree`), and `houseRole` — the type's entry in
 * `IBarSpiritSettings.houseRoles`, recorded by Bar → House pours — says which
 * role is the type's house when it has several (White / Dark / Aged Rum):
 * - recorded `BAR_HOUSE_POUR_NONE`: `null` — cleared in Bar → House pours;
 * - recorded role: the holder of THAT role, or `null` when no bottle of this
 *   type holds it (its bottle un-carried with no heir) — never another role's
 *   holder;
 * - never recorded: the role holders first by name, then inventory guid —
 *   deterministic, so the call list, the preview and the recipe line agree.
 *   For a type with one role (most) that is simply its holder.
 */
/**
 * `IBarSpiritSettings.houseRoles[type]` meaning "this type has NO house pour"
 * — the manager cleared it in Bar → House pours. It is a RECORD, not a role
 * move: the bottle holding the role keeps it, so every adopted catalog
 * cocktail that pours that role is unaffected (design A1), and a bottle
 * carried later never silently becomes the house.
 */
export const BAR_HOUSE_POUR_NONE = "none";

export function barHousePourFor(
  key: BarSpiritType,
  carried: ReadonlyArray<SpiritListCarriedItem>,
  houseRole?: string,
): SpiritListCarriedItem | null {
  const holders = carried
    .filter((c) => c.barSpiritType === key && !isMixer(c) && !!c.platformIngredientGuid)
    .sort((a, b) => {
      const byName = (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
      return byName !== 0 ? byName : a.inventoryGuid.localeCompare(b.inventoryGuid);
    });
  if (houseRole === BAR_HOUSE_POUR_NONE) return null;
  // A "same as" record is not a role: the type has no house bottle of its own.
  if (houseRole !== undefined && barHouseSameAsOf(houseRole)) return null;
  if (houseRole !== undefined) return holders.find((c) => c.platformIngredientGuid === houseRole) ?? null;
  return holders[0] ?? null;
}

// ── "Same as <type>" house pours (House of Pho, 2026-10-06) ─────────────────
//
// "Most places tend to use 1 bourbon for scotch and whisky … use bourbon
// default, because [listing every spirit] could be a long giant list." A
// type's house pour may be ANOTHER type's house bottle. Stored in the SAME
// per-type record as the house role (`IBarSpiritSettings.houseRoles[type]`),
// as `same_as:<type>` — beside the `none` record — so a type has exactly one
// stored choice (a role, none, or same-as) and no two fields can disagree.
// It claims no role: the target's bottle keeps its own role, and
// `assertHousePourFree` is untouched.

/** The prefix of a "same as" record in `houseRoles` (`same_as:bourbon`). */
export const BAR_HOUSE_SAME_AS_PREFIX = "same_as:";

/**
 * The "same as" a type resolves by when it has NO stored choice (never set in
 * Bar → House pours): Whiskey and Scotch pour the house bourbon. Cognac and
 * Liqueur — and every other type — default to their own bottle by role, as
 * before. An explicit `none` is never defaulted.
 */
export const BAR_HOUSE_SAME_AS_DEFAULTS: Readonly<Partial<Record<BarSpiritType, BarSpiritType>>> = {
  whiskey: "bourbon",
  scotch: "bourbon",
};

/** The `houseRoles` record meaning "same as `target`". */
export function barHouseSameAsRecord(target: BarSpiritType): string {
  return `${BAR_HOUSE_SAME_AS_PREFIX}${target}`;
}

/** The type a `houseRoles` record says "same as", or `undefined` when it is a role / `none` / malformed. */
export function barHouseSameAsOf(recorded: string | undefined): BarSpiritType | undefined {
  if (!recorded?.startsWith(BAR_HOUSE_SAME_AS_PREFIX)) return undefined;
  const target = recorded.slice(BAR_HOUSE_SAME_AS_PREFIX.length);
  return (BAR_SPIRIT_TYPES as readonly string[]).includes(target) ? (target as BarSpiritType) : undefined;
}

type HouseRoles = Partial<Record<BarSpiritType, string>> | undefined;

/** The "same as" a type is under — its stored record, else its default when nothing is stored. */
export function barHouseSameAsTarget(
  key: BarSpiritType,
  houseRoles: HouseRoles,
): { readonly target: BarSpiritType; readonly defaulted: boolean } | undefined {
  const recorded = houseRoles?.[key];
  if (recorded !== undefined) {
    const target = barHouseSameAsOf(recorded);
    return target ? { target, defaulted: false } : undefined;
  }
  const fallback = BAR_HOUSE_SAME_AS_DEFAULTS[key];
  return fallback ? { target: fallback, defaulted: true } : undefined;
}

/**
 * A spirit type's house pour, resolved — THE resolver. `house` is the bottle
 * a mixed drink of this type with no call pick pours (and so what its recipe
 * line depletes), `null` when it has none. `sameAs` names the type it pours
 * the same as (`defaulted` when that came from `BAR_HOUSE_SAME_AS_DEFAULTS`).
 *
 * Same-as is followed ONE level: when the target is itself "same as" (a chain
 * or a cycle), or has no house, this type has none. Never a guess.
 */
export interface BarHouseResolution {
  readonly house: SpiritListCarriedItem | null;
  readonly sameAs?: BarSpiritType;
  readonly defaulted?: true;
}

export function resolveBarHouse(
  key: BarSpiritType,
  carried: ReadonlyArray<SpiritListCarriedItem>,
  houseRoles: HouseRoles,
): BarHouseResolution {
  const same = barHouseSameAsTarget(key, houseRoles);
  if (!same) return { house: barHousePourFor(key, carried, houseRoles?.[key]) };
  const how = { sameAs: same.target, ...(same.defaulted ? { defaulted: true as const } : {}) };
  if (same.target === key || barHouseSameAsTarget(same.target, houseRoles)) return { house: null, ...how };
  return { house: barHousePourFor(same.target, carried, houseRoles?.[same.target]), ...how };
}

/**
 * Why `key` may NOT be set "same as" `target`, or `undefined` when it may.
 * Refuses a type pointing at itself, at a type that is itself "same as" (a
 * chain), and a type that other types already pour the same as (setting it
 * would turn theirs into a chain). Defaults count: Whiskey and Scotch pour
 * the same as Bourbon until they are set otherwise.
 */
export function barHouseSameAsProblem(
  key: BarSpiritType,
  target: BarSpiritType,
  houseRoles: HouseRoles,
): { readonly code: "self" | "chain" | "is_target"; readonly message: string } | undefined {
  const label = (t: BarSpiritType) => BAR_SPIRIT_TYPE_LABELS[t];
  if (target === key) return { code: "self", message: `${label(key)} cannot pour the same as itself.` };
  const targetSame = barHouseSameAsTarget(target, houseRoles);
  if (targetSame) {
    return {
      code: "chain",
      message: `${label(target)} already pours the same as ${label(targetSame.target)}. Pick ${label(targetSame.target)} itself, or give ${label(target)} its own bottle first.`,
    };
  }
  const pointing = BAR_SPIRIT_TYPES.filter((t) => t !== key && barHouseSameAsTarget(t, houseRoles)?.target === key);
  if (pointing.length > 0) {
    return {
      code: "is_target",
      message: `${pointing.map(label).join(" and ")} pour${pointing.length === 1 ? "s" : ""} the same as ${label(key)}, so ${label(key)} needs a bottle of its own. Change ${pointing.length === 1 ? "it" : "them"} first.`,
    };
  }
  return undefined;
}

/**
 * The "Same as …" choices Bar → House pours offers a type: every OTHER type
 * that has a real house bottle of its OWN (not itself "same as"), in type
 * order — a short list, never every spirit. A type others already pour the
 * same as gets none (the choice would be refused, `barHouseSameAsProblem`).
 */
export function barHouseSameAsOptions(
  key: BarSpiritType,
  carried: ReadonlyArray<SpiritListCarriedItem>,
  houseRoles: HouseRoles,
): Array<{ readonly spiritType: BarSpiritType; readonly house: SpiritListCarriedItem }> {
  if (BAR_SPIRIT_TYPES.some((t) => t !== key && barHouseSameAsTarget(t, houseRoles)?.target === key)) return [];
  const out: Array<{ spiritType: BarSpiritType; house: SpiritListCarriedItem }> = [];
  for (const t of BAR_SPIRIT_TYPES) {
    if (t === key || barHouseSameAsTarget(t, houseRoles)) continue;
    const house = barHousePourFor(t, carried, houseRoles?.[t]);
    if (house) out.push({ spiritType: t, house });
  }
  return out;
}

/** Every type each bottle is the resolved house for, in type order — the Bottles tab's "House pour: Bourbon, Whiskey, Scotch". */
export function barHouseTypesByBottle(
  carried: ReadonlyArray<SpiritListCarriedItem>,
  houseRoles: HouseRoles,
): Map<string, BarSpiritType[]> {
  const out = new Map<string, BarSpiritType[]>();
  for (const t of BAR_SPIRIT_TYPES) {
    const { house } = resolveBarHouse(t, carried, houseRoles);
    if (!house) continue;
    out.set(house.inventoryGuid, [...(out.get(house.inventoryGuid) ?? []), t]);
  }
  return out;
}

/**
 * The bottles Bar → House pours offers as a spirit type's house pour: every
 * carried, non-mixer item of that type, by name — the SAME set a house can be
 * (`barHousePourFor` never picks anything else).
 */
export function barHousePourCandidates(
  key: BarSpiritType,
  carried: ReadonlyArray<SpiritListCarriedItem>,
): SpiritListCarriedItem[] {
  return carried
    .filter((c) => c.barSpiritType === key && !isMixer(c))
    .sort((a, b) => {
      const byName = (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
      return byName !== 0 ? byName : a.inventoryGuid.localeCompare(b.inventoryGuid);
    });
}

/**
 * The role `candidate` holds once Bar → House pours makes it its type's house
 * (design A1), or `undefined` when there is none to give it. Moving a role
 * changes what every adopted cocktail pouring it depletes, so the order
 * prefers the role that is ALREADY the type's house:
 * 1. the role it already holds — no role moves at all (Absolut Citron stays
 *    Citrus Vodka's; it only becomes the VODKA house);
 * 2. the current house's role (Vodka moves from Absolut to Tito's);
 * 3. the role recorded for the type (its holder was un-carried);
 * 4. its catalog row's role (Absolut → Vodka) — the first house of a type.
 * `BAR_HOUSE_POUR_NONE` is not a role and is never returned.
 */
export function barHousePourRoleFor(
  candidate: SpiritListCarriedItem,
  currentHouse: SpiritListCarriedItem | null,
  houseRole: string | undefined,
): string | undefined {
  return (
    candidate.platformIngredientGuid ??
    currentHouse?.platformIngredientGuid ??
    (houseRole !== BAR_HOUSE_POUR_NONE && !barHouseSameAsOf(houseRole) ? houseRole : undefined) ??
    candidate.bottle?.platformIngredientGuid
  );
}

/**
 * The generated groups a mixed-drink item (VODKA, RUM, …) carries, in ring
 * order — THE canonical order, read by every writer of those references:
 * its CALL LIST first (optional — no pick pours the house bottle), then the
 * REQUIRED Style when Style is on (its mixer choices included — the separate
 * Mixer group is retired, 2026-10-03), then Double when Double is on (owner,
 * 2026-10-09: "When choosing the spirits, set Bar Call Spirits FIRST, then
 * Style." — this REPLACES 2026-10-04's "the Style should be position 0 and
 * the spirits position should be 1"). The till opens on the call list
 * because its generated group carries `includePosPrompt`
 * (`barListGroupPromptsOnPos`); the pick stays optional. Only these slots are
 * the generator's on such an item; every other reference is the
 * restaurant's and rings after them.
 */
export function barMixedDrinkGroupSlots(
  key: BarSpiritType,
  settings: BarListOnSettings,
): BarListGroupSlot[] {
  const slots: BarListGroupSlot[] = [];
  if (isBarListOn(settings, key)) slots.push(key);
  if (isBarListOn(settings, "style")) slots.push("style_required");
  if (isBarListOn(settings, "double")) slots.push("double");
  return slots;
}

/**
 * Is a generated group of this slot created with `includePosPrompt` on
 * (management's "Include POS Prompt") — the till presents it unasked? True
 * for a SPIRIT call list (`isBarSpiritListKey`: the nine spirit types,
 * Liqueur included — each is a call list of brands); false for Style, Style
 * required, Double and the retired Mixer list (owner, 2026-10-09: the till
 * opens a mixed drink on its call list, which is optional and would
 * otherwise never be shown). The flag is per GROUP, so the prompt follows the
 * call list wherever it is attached — a catalog cocktail carrying it too.
 * Written once (at creation, or once on an existing group —
 * `IBarListGroupProvenance.posPromptSeededAt`); afterwards it is the
 * restaurant's.
 */
export function barListGroupPromptsOnPos(slot: BarListGroupSlot): boolean {
  return slot !== "style_required" && isBarSpiritListKey(slot);
}

/** The selection rules a generated group is created with. */
export interface BarListGroupRules {
  readonly selectionRequirement: "Optional" | "Required";
  readonly minSelection: number;
  readonly maxSelection: number;
  readonly allowMultipleSelection: boolean;
}

/**
 * The selection rules of one generated group, by SLOT. Every spirit list picks
 * 0–1 (with nothing picked the drink pours the house bottle — there is no
 * required spirit group); Mixer picks 0–2; Style and Double 0–1. Only
 * `style_required` (the mixed-drink items' Style) picks exactly 1.
 */
export function barListGroupRules(slot: BarListGroupSlot): BarListGroupRules {
  if (slot === "mixer") {
    return { selectionRequirement: "Optional", minSelection: 0, maxSelection: 2, allowMultipleSelection: true };
  }
  return slot === "style_required"
    ? { selectionRequirement: "Required", minSelection: 1, maxSelection: 1, allowMultipleSelection: false }
    : { selectionRequirement: "Optional", minSelection: 0, maxSelection: 1, allowMultipleSelection: false };
}
